import { Component, computed, DestroyRef, effect, inject, signal, viewChild, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../core/services/auth-service';
import { SubTenantService } from '../../core/services/subtenant-service';
import { ChatFeedbackService } from '../../core/services/chat-feedback-service';
import { tenant } from '../../../tenants/tenant';
import { firstValueFrom } from 'rxjs';
import { NexaChatComponent } from '../../features/nexa-chat/nexa-chat.component';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-test-chat-screen',
  imports: [
    CommonModule,
    NexaChatComponent,
    TranslatePipe,
  ],
  templateUrl: './test-chat-screen.html',
  styleUrl: './test-chat-screen.css',
})
export class TestChatScreen implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly subTenantService = inject(SubTenantService);
  private readonly chatFeedback = inject(ChatFeedbackService);
  private readonly destroyRef = inject(DestroyRef);

  widget = viewChild<NexaChatComponent>('chat');
  accessType = signal<'private' | 'public'>('public');
  chatType = signal<string>('widget');
  isStandalone = computed(() => this.chatType() === 'standalone');
  showHeader = computed(() => this.isStandalone() && this.accessType() === 'public');
  accessToken = signal<string | null>(null);
  chatIntro = computed(() => {
    if (this.accessType() !== 'private') {
      return '';
    }
    return `Name: ${this.authService.user()?.name ?? ''}`;
  });
  subTenantId = signal<string | null>(null);
  subTenantName = signal<string | null>(null);
  loaded = signal(false);

  private tokenRefreshTimer?: ReturnType<typeof setTimeout>;

  readonly headerColor = tenant.theme?.semantic?.primary?.[500] ?? '#003B55';

  ngOnInit() {
    console.log('[TestChatScreen] Component initialized');
    const path = this.route.snapshot.routeConfig?.path || '';
    this.accessType.set(path.includes('private') ? 'private' : 'public');
    this.chatType.set(
      this.route.snapshot.data['type'] ?? this.route.snapshot.params['type'] ?? 'widget'
    );
    if (this.accessType() === 'private' && this.isStandalone()) {
      this.chatFeedback.register(() => this.showFeedback(), this.destroyRef);
    }
    this.subTenantId.set(this.resolveSubTenantId());
    this.subTenantName.set(this.resolveSubTenantName());

    console.log('[TestChatScreen] Sub-tenant id:', this.subTenantId(), 'name:', this.subTenantName());
    
    console.log('[TestChatScreen] Access type:', this.accessType());
    console.log('[TestChatScreen] Chat type:', this.chatType());
    
    this.initializeChat();
  }

  private resolveSubTenantId(): string | null {
    const selectedSubTenantId = this.subTenantService.getSelectedSubtenantId();
    if (selectedSubTenantId) {
      return selectedSubTenantId;
    }
    return this.route.snapshot.data['subTenantId'] ?? null;
  }

  private resolveSubTenantName(): string | null {
    const selectedSubTenantName = this.subTenantService.getSelectedSubtenantName();
    if (selectedSubTenantName) {
      return selectedSubTenantName;
    }
    const routeSubTenantName = this.route.snapshot.data['subTenantName'] ?? null;
    if (routeSubTenantName) {
      return routeSubTenantName;
    }
    const id = this.subTenantId();
    if (!id) {
      return null;
    }
    return this.subTenantService.getAvailableSubtenants().find(s => s.id === id)?.name ?? null;
  }

  async initializeChat() {
    try {
      console.log('[TestChatScreen] Initializing chat, access type:', this.accessType());
      
      if (this.accessType() === 'private') {
        await this.authService.ensureUserProfile();

        const token = await firstValueFrom(this.authService.getValidToken());

        console.log('[TestChatScreen] Private mode - User:', this.authService.user());

        this.accessToken.set(token);
        this.scheduleTokenRefresh(token);
        this.loaded.set(true);
      } else if (this.accessType() === 'public') {
        console.log('[TestChatScreen] Public mode - loading chat');
        this.accessToken.set(null);
        this.loaded.set(true);
      }
      
      console.log('[TestChatScreen] Chat loaded:', this.loaded());
    } catch (error) {
      console.error('[TestChatScreen] Error initializing chat:', error);
      this.loaded.set(true);
    }
  }

  private scheduleTokenRefresh(token: string | null): void {
    clearTimeout(this.tokenRefreshTimer);
    if (!token) {
      return;
    }
    const expiryMs = this.getTokenExpiryMs(token);
    if (!expiryMs) {
      return;
    }
    const REFRESH_BUFFER_MS = 60_000;
    const delay = Math.max(expiryMs - Date.now() - REFRESH_BUFFER_MS, 0);
    this.tokenRefreshTimer = setTimeout(async () => {
      const newToken =
        (await this.authService.refreshAppToken()) ??
        (await firstValueFrom(this.authService.getValidToken()));
      if (newToken) {
        this.accessToken.set(newToken);
        this.scheduleTokenRefresh(newToken);
      }
    }, delay);
  }

  private getTokenExpiryMs(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.tokenRefreshTimer);
  }

  showFeedback() {
    this.widget()?.showFeedback();
  }
}
